/*
This component is used to select items
It is used within the "share" page, for creating links
as well as the wishlist page, for adding items to the wishlist
It is also be used in the wishlist, for purchasing items or viewing details 
*/

import React from "react";
import config from "../../config";
import PropTypes from "prop-types";
import axios from "axios";
import "./ItemModal.css";
import { useState, useEffect, useRef, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useParams } from "react-router-dom";
import { AiOutlineClose } from "react-icons/ai";
import { useItemPurchase } from "./useItemPurchase";
import {format} from 'timeago.js';
import Popup from "../popup/Popup";


export default function ItemModal({
  item,
  showModal,
  setShowModal,
  onItemClick,
  CTAButton,
  CTAFunction,
  closeModal,
  onPurchaseSuccess,
  setConversationId,
  setShowTopbar,
  setIsAccountBalanceModalOpen
}) {
  if (!showModal) return null;

  const { balance } = useContext(AuthContext);
  const [showPopup, setShowPopup] = useState(false);
  const [itemPrice, setItemPrice] = useState();
  const [shippingPrice, setShippingPrice] = useState();
  const [itemTotalPrice, setItemTotalPrice] = useState();
  const [itemPurchaseData, setItemPurchaseData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [popupMessage, setPopupMessage] = useState("");
  const [buttonOneText, setButtonOneText] = useState("");
  const [buttonOneFunc, setButtonOneFunc] = useState(() => {});
  const [buttonTwoText, setButtonTwoText] = useState("");
  const [buttonTwoFunc, setButtonTwoFunc] = useState(() => {});
  // Account for two different ways to pass the image URL
  const [currentImage, setCurrentImage] = useState();
  const [triggerPurchase, setTriggerPurchase] = useState(false);
  const triggerPurchaseRef = useRef(false); // To avoid double renders
  const activeWishlistUsername = useParams().username;
  const [updatedImages, setUpdatedImages] = useState([]);

  const popupStatus = async (
    message,
    buttonOne,
    buttonOneFunc,
    buttonTwo,
    buttonTwoFunc
  ) => {
    setPopupMessage(message);
    setButtonOneText(buttonOne);
    setButtonOneFunc(() => buttonOneFunc); // Assuming buttonOneFunc is a function
    setButtonTwoText(buttonTwo);
    setButtonTwoFunc(() => buttonTwoFunc); // Assuming buttonTwoFunc is a function
    setShowPopup(true);
  };

  // custom hook for handling purchases
  useItemPurchase(
    item,
    itemTotalPrice,
    popupStatus,
    setShowPopup,
    setShowModal,
    triggerPurchase,
    onPurchaseSuccess,
    setConversationId
  );

  // update stock images to be higher quality
  useEffect(() => {
    if (item.allImages && Array.isArray(item.allImages) && item.allImages.length > 0) {
      setUpdatedImages(item.allImages);
      setCurrentImage(item.allImages[0]);
    } else {
      console.log("No image Url found...");
    }
  }, [item]);

  useEffect(() => {
    if (item.purchased === true){

      const getTransactionData = async () => {
        setIsLoading(true);
        const token = localStorage.getItem("token");
        const res = await axios.get(`${config.apiUrl}/wishlists/get-transaction`, {
          headers: {
            'x-auth-token': token
          }, 
          params: {
            itemId: item.itemId,
            wishlistUsername: activeWishlistUsername
          }
          })
          setItemPurchaseData(res.data);
          setIsLoading(false);  // No fetch needed, not loading
      };

      getTransactionData();
    } else {
      setItemPurchaseData(null);
      setIsLoading(false);  // No fetch needed, not loading
    }
  }, [item])

  useEffect(() => {
    const itemPrice = parseFloat(item.price);
    if (item.shippingPrice === "Calculated") {
      // TODO: pass the calulated amount to itemTotalPrice, which is used in useItemPurchase above.
      // Shipping address will be needed

      // used in useItemPurchase
      setItemTotalPrice(itemPrice);
      // used in modal
      setItemPrice(itemPrice);
      setShippingPrice("Calculated");
    } else {
      const itemShipping = parseFloat(item.shippingPrice);
      const itemPricePlusShipping = itemPrice + itemShipping;
      const roundedTotalCost = itemPricePlusShipping.toFixed(2);
      // used in useItemPurchase hook
      setItemTotalPrice(`${roundedTotalCost}`);
      // used in modal
      setItemPrice(itemPrice);
      setShippingPrice(itemShipping);
    }
  }, [item]);

  const handleImageClick = (image) => {
    setCurrentImage(image);
  };

  const combinedClickHandler = (item) => {
    if (CTAFunction === "Purchase" && balance > itemTotalPrice) {
      if (!triggerPurchaseRef.current) {
        setTriggerPurchase(true);
        triggerPurchaseRef.current = true; // Avoid re-triggering on state update
      }
    } else if (CTAFunction === "Close") {
      closeModal();
    } else if (CTAFunction === "Select") {
      // this has the allImages array
      onItemClick(item);
    }
  };

  const goToAddFunds = () => {
    setShowModal(false);
    setIsAccountBalanceModalOpen(true);
    setShowTopbar(false);
  };
  
  return (
    <div className="item-modal">
      {showPopup && (
        <Popup
          className="popup"
          isPopupOpen={showPopup}
          message={popupMessage}
          button1Text={buttonOneText}
          button1Action={buttonOneFunc}
          button2Text={buttonTwoText}
          button2Action={buttonTwoFunc}
        />
      )}
      <div className="item-modal-content" onClick={(e) => e.stopPropagation()}>
        <AiOutlineClose
          className="closebutton"
          onClick={(e) => {
            e.stopPropagation();
            closeModal();
          }}
        >
          Close
        </AiOutlineClose>

        <img className="item-modal-image" src={currentImage} alt={item.title} />
        
        {/* image carousel */}
        {updatedImages && updatedImages.length > 0 && (
          <div className="image-carousel">
            {updatedImages.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`Additional Image ${index}`}
                className="additional-img"
                onClick={() => handleImageClick(image)}
              />
            ))}
          </div>
        )}

        <div className="title-wrap">
          <h2>{item.name}</h2>
        </div>

        <div className="item-details">
          {isLoading ? (
            <p>Loading...</p>  // Optional: Loading indicator
          ) : (
            itemPurchaseData ? (
              <p>Purchased {format(itemPurchaseData.wishlistTransactionData.createdAt)}</p>
            ) : (
              <>
                <p>Price: ${itemPrice}</p>
                {shippingPrice !== "Calculated" ? (
                  <p>Shipping: ${shippingPrice}</p>
                ) : (
                  <p>Shipping: {shippingPrice}</p>
                )}
                {item.condition && <p>Condition: {item.condition}</p>}
                <div className="item-description-container">
                  <p className="item-modal-description">{item.description}</p>
                </div>
              </>
            )
          )}
          </div>

        <div className="item-modal-button-container">
          {CTAButton === "Purchase" ? (
            balance > itemTotalPrice ? (
              <button
                className="giveButton"
                onClick={() => combinedClickHandler(item)}
                >
                {CTAButton}
              </button>
            ) : (
              <button
                className="giveButton"
                onClick={goToAddFunds}
                >
                Add Funds
              </button>
            )  
          ) : (
            <button
              className="giveButton"
              onClick={() => combinedClickHandler(item)}
              >
              {CTAButton}
            </button>
          )}

        </div>
      </div>
    </div>
  );
}

ItemModal.propTypes = {
  item: PropTypes.object.isRequired,
  showModal: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired,
  post: PropTypes.object,
  setItemGives: PropTypes.func,
  setChangeItemIconColor: PropTypes.func,
  onGive: PropTypes.func,
  socket: PropTypes.object,
  setseenGifts: PropTypes.func,
  onItemClick: PropTypes.func,
  CTAButton: PropTypes.string.isRequired,
  CTAFunction: PropTypes.string.isRequired,
  closeModal: PropTypes.func,
  onPurchaseSuccess: PropTypes.func,
  setConversationId: PropTypes.func,
  setShowTopbar: PropTypes.func, 
  setIsAccountBalanceModalOpen: PropTypes.func,

};
